import { useState, useMemo } from 'react';
import { packages, type Package } from '@/mocks/packages';
import { customers as allCustomers } from '@/mocks/customers';
import CustomerCombobox from '@/components/base/CustomerCombobox';

export default function PaketlerPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pkgList, setPkgList] = useState<Package[]>(packages);
  const [detailPkg, setDetailPkg] = useState<Package | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  const [newForm, setNewForm] = useState({
    customerName: '',
    name: '',
    service: 'Masaj',
    totalSessions: 6,
    remainingSessions: 6,
    price: 0,
    startDate: '2026-05-08',
    endDate: '2026-06-08',
    giftKeseKopuk: false,
    giftKeseKopukUsed: false,
    renewReminder: false,
    notes: '',
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAddPackage = () => {
    if (!newForm.customerName.trim() || !newForm.name.trim() || newForm.price <= 0) {
      showToast('Lütfen tüm zorunlu alanları doldurun', 'error');
      return;
    }
    const newPkg: Package = {
      id: Date.now().toString(),
      customerName: newForm.customerName.trim(),
      name: newForm.name.trim(),
      service: newForm.service,
      totalSessions: newForm.totalSessions,
      remainingSessions: newForm.remainingSessions,
      price: newForm.price,
      startDate: newForm.startDate,
      endDate: newForm.endDate,
      active: newForm.remainingSessions > 0,
      lastSessionDate: null,
      giftKeseKopuk: newForm.giftKeseKopuk,
      giftKeseKopukUsed: newForm.giftKeseKopukUsed,
      renewReminder: newForm.renewReminder,
      notes: newForm.notes.trim() || null,
    };
    setPkgList(prev => [newPkg, ...prev]);
    setShowAddModal(false);
    setNewForm({
      customerName: '', name: '', service: 'Masaj', totalSessions: 6, remainingSessions: 6, price: 0,
      startDate: '2026-05-08', endDate: '2026-06-08', giftKeseKopuk: false, giftKeseKopukUsed: false,
      renewReminder: false, notes: '',
    });
    showToast('Paket başarıyla oluşturuldu');
  };

  const filtered = useMemo(() => {
    let data = [...pkgList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(p => p.customerName.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') data = data.filter(p => p.active && p.remainingSessions > 0);
      if (statusFilter === 'low') data = data.filter(p => p.active && p.remainingSessions <= 2 && p.remainingSessions > 0);
      if (statusFilter === 'expired') data = data.filter(p => !p.active || p.remainingSessions === 0);
      if (statusFilter === 'gift_unused') data = data.filter(p => p.giftKeseKopuk && !p.giftKeseKopukUsed);
      if (statusFilter === 'renew') data = data.filter(p => p.renewReminder);
    }
    return data;
  }, [pkgList, searchQuery, statusFilter]);

  const handleUseSession = (id: string) => {
    setPkgList(prev => prev.map(p => {
      if (p.id !== id) return p;
      const newRemaining = Math.max(0, p.remainingSessions - 1);
      return { ...p, remainingSessions: newRemaining, active: newRemaining > 0, lastSessionDate: '2026-05-08' };
    }));
  };

  const handleGiftUsed = (id: string) => {
    setPkgList(prev => prev.map(p => p.id === id ? { ...p, giftKeseKopukUsed: true } : p));
  };

  const stats = useMemo(() => ({
    total: pkgList.length,
    active: pkgList.filter(p => p.active && p.remainingSessions > 0).length,
    low: pkgList.filter(p => p.active && p.remainingSessions <= 2 && p.remainingSessions > 0).length,
    expired: pkgList.filter(p => !p.active || p.remainingSessions === 0).length,
    giftUnused: pkgList.filter(p => p.giftKeseKopuk && !p.giftKeseKopukUsed).length,
  }), [pkgList]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Paketler / Seanslar</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} paket kaydı</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Paket
        </button>
      </div>

      {/* Alert Banner */}
      {(stats.low > 0 || stats.giftUnused > 0) && (
        <div className="flex flex-wrap gap-2">
          {stats.low > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-spa-sm text-xs font-medium" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.2)' }}>
              <i className="ri-alarm-warning-line" /> {stats.low} pakette 2 seanstan az kaldı
            </div>
          )}
          {stats.giftUnused > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-spa-sm text-xs font-medium" style={{ background: 'rgba(201,169,98,0.12)', color: '#C9A962', border: '1px solid rgba(201,169,98,0.2)' }}>
              <i className="ri-gift-line" /> {stats.giftUnused} kese köpük hediyesi kullanılmadı
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: stats.total, icon: 'ri-vip-crown-line', color: '#C9A962' },
          { label: 'Aktif', value: stats.active, icon: 'ri-check-line', color: '#10B981' },
          { label: 'Seansı Azalan', value: stats.low, icon: 'ri-alarm-warning-line', color: '#F59E0B' },
          { label: 'Biten', value: stats.expired, icon: 'ri-time-line', color: '#EF4444' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Müşteri veya paket adı ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <select className="spa-input text-sm min-w-[160px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">Tüm Paketler</option>
          <option value="active">Aktif Paketler</option>
          <option value="low">Seansı Azalan (&le;2)</option>
          <option value="expired">Biten / Pasif</option>
          <option value="gift_unused">Kese Köpük Kullanılmadı</option>
          <option value="renew">Yenileme Hatırlatması</option>
        </select>
      </div>

      {/* Package Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map(pkg => {
          const progress = pkg.totalSessions > 0 ? (pkg.remainingSessions / pkg.totalSessions) * 100 : 0;
          const isLow = pkg.remainingSessions <= 2 && pkg.remainingSessions > 0;
          const isExpired = !pkg.active || pkg.remainingSessions === 0;
          return (
            <div key={pkg.id} className="spa-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{pkg.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{pkg.customerName}</p>
                </div>
                <span className="spa-badge" style={{ background: isExpired ? 'rgba(156,163,175,0.12)' : isLow ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)', color: isExpired ? '#9CA3AF' : isLow ? '#F59E0B' : '#10B981' }}>
                  {isExpired ? 'Pasif' : `${pkg.remainingSessions} / ${pkg.totalSessions}`}
                </span>
              </div>

              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: isExpired ? '#9CA3AF' : isLow ? '#F59E0B' : 'var(--gold)' }} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>Başlangıç</p>
                  <p style={{ color: 'var(--text-primary)' }}>{pkg.startDate}</p>
                </div>
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>Bitiş</p>
                  <p style={{ color: isExpired ? '#EF4444' : 'var(--text-primary)' }}>{pkg.endDate}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {pkg.giftKeseKopuk && (
                  <span className="spa-badge text-[10px]" style={{ background: pkg.giftKeseKopukUsed ? 'rgba(16,185,129,0.12)' : 'rgba(201,169,98,0.12)', color: pkg.giftKeseKopukUsed ? '#10B981' : '#C9A962' }}>
                    <i className="ri-gift-line mr-1" />{pkg.giftKeseKopukUsed ? 'Kese Köpük Kullanıldı' : 'Kese Köpük Hediye'}
                  </span>
                )}
                {pkg.renewReminder && (
                  <span className="spa-badge text-[10px]" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>
                    <i className="ri-refresh-line mr-1" />Yenileme Hatırlatma
                  </span>
                )}
              </div>

              <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                {!isExpired && (
                  <button onClick={() => handleUseSession(pkg.id)} className="flex-1 spa-btn-primary text-xs py-2">
                    <i className="ri-check-line mr-1" />Seans Kullan
                  </button>
                )}
                {pkg.giftKeseKopuk && !pkg.giftKeseKopukUsed && (
                  <button onClick={() => handleGiftUsed(pkg.id)} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(201,169,98,0.12)', color: '#C9A962' }}>
                    <i className="ri-gift-line mr-1" />Hediye Kullan
                  </button>
                )}
                <button onClick={() => setDetailPkg(pkg)} className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}>
                  <i className="ri-eye-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-vip-crown-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Paket bulunamadı</p>
        </div>
      )}

      {/* Detail Modal */}
      {detailPkg && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setDetailPkg(null)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-md flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Paket Detayı</h3>
              <button onClick={() => setDetailPkg(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-3 text-sm overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Paket</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.name}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Müşteri</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.customerName}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Hizmet</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.service}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Paket Bedeli</p><p style={{ color: 'var(--text-primary)' }}>₺{detailPkg.price.toLocaleString('tr-TR')}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Toplam Seans</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.totalSessions}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Kalan Seans</p><p style={{ color: detailPkg.remainingSessions <= 2 ? '#EF4444' : 'var(--text-primary)' }}>{detailPkg.remainingSessions}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Başlangıç</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.startDate}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Bitiş</p><p style={{ color: 'var(--text-primary)' }}>{detailPkg.endDate}</p></div>
              </div>
              <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Son Seans Tarihi</p>
                <p style={{ color: 'var(--text-primary)' }}>{detailPkg.lastSessionDate || 'Henüz kullanılmadı'}</p>
              </div>
              {detailPkg.notes && (
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Notlar</p>
                  <p style={{ color: 'var(--text-primary)' }}>{detailPkg.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Package Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setShowAddModal(false)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-lg flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Yeni Paket Oluştur</h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-3 text-sm overflow-y-auto flex-1 min-h-0">
              <CustomerCombobox
                customers={allCustomers.map(c => ({ id: c.id, fullName: c.fullName, phone: c.phone, email: c.email, status: c.status }))}
                value={newForm.customerName ? { id: '', fullName: newForm.customerName, phone: '' } : null}
                onChange={(customer) => {
                  if (customer) {
                    setNewForm(prev => ({ ...prev, customerName: customer.fullName }));
                  }
                }}
                onCreateNew={(name) => {
                  setNewForm(prev => ({ ...prev, customerName: name }));
                }}
                label="Müşteri"
                required
              />
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Paket Adı *</label>
                <input
                  className="spa-input w-full"
                  placeholder="Paket adı"
                  value={newForm.name}
                  onChange={e => setNewForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Hizmet</label>
                  <select
                    className="spa-input w-full"
                    value={newForm.service}
                    onChange={e => setNewForm(prev => ({ ...prev, service: e.target.value }))}
                  >
                    <option value="Masaj">Masaj</option>
                    <option value="Spa">Spa</option>
                    <option value="Kese Köpük">Kese Köpük</option>
                    <option value="Sauna">Sauna</option>
                    <option value="Buhar">Buhar</option>
                    <option value="Tuz Odası">Tuz Odası</option>
                    <option value="Paket Hizmet">Paket Hizmet</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Paket Bedeli *</label>
                  <input
                    type="number"
                    className="spa-input w-full"
                    placeholder="0"
                    value={newForm.price || ''}
                    onChange={e => setNewForm(prev => ({ ...prev, price: Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Toplam Seans</label>
                  <input
                    type="number"
                    className="spa-input w-full"
                    value={newForm.totalSessions}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setNewForm(prev => ({ ...prev, totalSessions: val, remainingSessions: val }));
                    }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Kalan Seans</label>
                  <input
                    type="number"
                    className="spa-input w-full"
                    value={newForm.remainingSessions}
                    onChange={e => setNewForm(prev => ({ ...prev, remainingSessions: Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Başlangıç Tarihi</label>
                  <input
                    type="date"
                    className="spa-input w-full"
                    value={newForm.startDate}
                    onChange={e => setNewForm(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Bitiş Tarihi</label>
                  <input
                    type="date"
                    className="spa-input w-full"
                    value={newForm.endDate}
                    onChange={e => setNewForm(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer accent-[#C9A962]"
                    checked={newForm.giftKeseKopuk}
                    onChange={e => setNewForm(prev => ({ ...prev, giftKeseKopuk: e.target.checked }))}
                  />
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Kese Köpük Hediyesi Ekle</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer accent-[#F59E0B]"
                    checked={newForm.renewReminder}
                    onChange={e => setNewForm(prev => ({ ...prev, renewReminder: e.target.checked }))}
                  />
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Yenileme Hatırlatması</span>
                </label>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Notlar</label>
                <textarea
                  className="spa-input w-full min-h-[80px] resize-none"
                  maxLength={500}
                  placeholder="Müşteri ile ilgili notlar..."
                  value={newForm.notes}
                  onChange={e => setNewForm(prev => ({ ...prev, notes: e.target.value }))}
                />
                <p className="text-[10px] text-right mt-1" style={{ color: 'var(--text-muted)' }}>{newForm.notes.length}/500</p>
              </div>
            </div>

            <div className="flex gap-2 p-5 pt-2 flex-shrink-0">
              <button onClick={() => setShowAddModal(false)} className="flex-1 spa-btn-secondary text-sm py-2.5">
                İptal
              </button>
              <button onClick={handleAddPackage} className="flex-1 spa-btn-primary text-sm py-2.5">
                <i className="ri-check-line mr-1" />Paket Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] animate-fade-in">
          <div
            className="px-4 py-3 rounded-spa-sm text-sm font-medium flex items-center gap-2"
            style={{
              background: toast.type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)',
              color: '#fff',
              backdropFilter: 'blur(12px)',
            }}
          >
            <i className={toast.type === 'error' ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}